import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

interface Variable {
  symbol: string;
  name: string;
  default_unit: string;
}

interface Equation {
  id: string;
  name: string;
  domain: string;
  tags: string[];
  meaning: string;
  symbolic_form: string;
  variables: Variable[];
  assumptions: string[];
}

interface SolveResult {
  equation_id: string;
  equation_name: string;
  solve_for: string;
  value: number;
  unit: string;
  value_sigfig: string;
  significant_figures: number;
  warnings: string[];
  assumptions: string[];
  working?: { symbolic_form?: string; solved_expression?: string; substituted_expression?: string };
}

const WorkbenchCalcs: React.FC = () => {
  const [equations, setEquations] = useState<Equation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [selectedEq, setSelectedEq] = useState<Equation | null>(null);
  const [solveFor, setSolveFor] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [units, setUnits] = useState<Record<string, string>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<SolveResult | null>(null);
  const [solveError, setSolveError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const { bailOutText, setHasSaved } = useToolStep();

  useEffect(() => {
    apiClient.get('/v2/calcs/equations')
      .then((resp) => {
        setEquations(resp.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load equations');
        setLoading(false);
      });
  }, []);

  const domains = React.useMemo(() => {
    const map: Record<string, Equation[]> = {};
    equations.forEach((eq) => {
      if (!map[eq.domain]) map[eq.domain] = [];
      map[eq.domain].push(eq);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [equations]);

  const selectEquation = (eq: Equation) => {
    setSelectedEq(eq);
    setResult(null);
    setSolveError('');
    // Default solve_for to the first variable
    setSolveFor(eq.variables[0]?.symbol || '');
    // Init units from defaults
    const u: Record<string, string> = {};
    eq.variables.forEach((v) => { u[v.symbol] = v.default_unit; });
    setUnits(u);
    setValues({});
  };

  const runSolve = async () => {
    if (!selectedEq || !solveFor) return;

    const knowns = selectedEq.variables
      .filter((v) => v.symbol !== solveFor && values[v.symbol]?.trim())
      .map((v) => ({
        symbol: v.symbol,
        value: parseFloat(values[v.symbol]),
        unit: units[v.symbol] || v.default_unit,
      }));

    if (knowns.length < selectedEq.variables.length - 1) {
      setSolveError(`Fill in all known variables (need ${selectedEq.variables.length - 1}, got ${knowns.length})`);
      return;
    }

    setIsRunning(true);
    setSolveError('');
    setResult(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiClient.post('/v2/calcs/solve', {
        equation_id: selectedEq.id,
        solve_for: solveFor,
        knowns,
      }, { signal: controller.signal });
      setResult(resp.data);
    } catch (err: any) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        const detail = err.response?.data?.detail || err.message || 'Solve failed';
        setSolveError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const goBack = () => {
    if (selectedEq) {
      setSelectedEq(null);
      setResult(null);
      setSolveError('');
    } else if (selectedDomain) {
      setSelectedDomain(null);
    }
  };

  // ---------- Breadcrumb ----------
  const breadcrumb = (
    <div style={statusBarStyle}>
      <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
      <span style={{ color: 'var(--card-border)' }}>|</span>
      {selectedDomain && !selectedEq && (
        <>
          <ToolSwitcher currentToolId="calcs" />
          <span style={{ color: 'var(--card-border)' }}>/</span>
          <span style={{ color: 'var(--text-primary)' }}>{selectedDomain}</span>
        </>
      )}
      {selectedEq && (
        <>
          <ToolSwitcher currentToolId="calcs" />
          <span style={{ color: 'var(--card-border)' }}>/</span>
          <button onClick={goBack} style={breadcrumbBtn}>{selectedEq.domain}</button>
          <span style={{ color: 'var(--card-border)' }}>/</span>
          <span style={{ color: 'var(--text-primary)' }}>{selectedEq.name}</span>
        </>
      )}
      {!selectedDomain && !selectedEq && (
        <ToolSwitcher currentToolId="calcs" />
      )}
      <span style={{ color: 'var(--card-border)' }}>|</span>
      <span>Engine: SymPy + Pint</span>
      <div style={{ flex: 1 }} />
      <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} />
    </div>
  );

  // ---------- Loading / Error ----------
  if (loading) {
    return (
      <>
        <WorkbenchContextBar />
        <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {breadcrumb}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <div style={spinnerStyle} /><span style={{ color: '#f59e0b' }}>Loading equations...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <WorkbenchContextBar />
        <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {breadcrumb}
            <div style={{ padding: 24 }}><div style={errorStyle}>{error}</div></div>
          </div>
        </div>
      </>
    );
  }

  // ---------- Domain list ----------
  if (!selectedDomain && !selectedEq) {
    return (
      <>
        <WorkbenchContextBar />
        <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {breadcrumb}
            <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={h2Style}>Equation Domains</h2>
              <p style={descStyle}>{equations.length} equations across {domains.length} domains. Pick a domain to browse.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                {domains.map(([domain, eqs]) => (
                  <button
                    key={domain}
                    onClick={() => setSelectedDomain(domain)}
                    style={domainCardStyle}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#f59e0b'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--card-border)'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>{domain}</span>
                      <span style={countBadge}>{eqs.length}</span>
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                      {eqs.slice(0, 3).map((e) => e.name).join(', ')}{eqs.length > 3 ? ', ...' : ''}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div style={{ width: 320, flexShrink: 0 }}>
              <MethodCard
                engine="SymPy"
                title="Symbolic Equation Solver"
                description="Solves equations symbolically using SymPy's computer algebra system. Input known values with units, select the unknown, and get an exact result with automatic unit conversion via Pint."
                parameters={[
                  { name: 'Domains', desc: `${domains.length} categories (PK, physical chem, ...)` },
                  { name: 'Equations', desc: `${equations.length} peer-reviewed equations` },
                  { name: 'Units', desc: 'Automatic conversion (Pint)' },
                  { name: 'Sig figs', desc: 'Tracked from input precision' },
                ]}
                reference="Meurer et al. (2017) PeerJ Computer Science"
                source="SymPy + Pint unit library"
              />
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  // ---------- Equation list for selected domain ----------
  if (selectedDomain && !selectedEq) {
    const domainEqs = equations.filter((eq) => eq.domain === selectedDomain);
    return (
      <>
        <WorkbenchContextBar />
        <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {breadcrumb}
            <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
              <h2 style={h2Style}>{selectedDomain}</h2>
            <p style={descStyle}>{domainEqs.length} equation{domainEqs.length !== 1 ? 's' : ''}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {domainEqs.map((eq) => (
                <button
                  key={eq.id}
                  onClick={() => selectEquation(eq)}
                  style={eqCardStyle}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#f59e0b'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--card-border)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>{eq.name}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{eq.meaning}</div>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', whiteSpace: 'nowrap', marginLeft: 12 }}>
                      {eq.variables.length} vars
                    </span>
                  </div>
                  <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 12, color: '#f59e0b' }}>
                    {eq.symbolic_form}
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                    {eq.tags.slice(0, 5).map((tag) => (
                      <span key={tag} style={tagStyle}>{tag}</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  // ---------- Equation solver view ----------
  const otherVars = selectedEq ? selectedEq.variables.filter((v) => v.symbol !== solveFor) : [];

  return (
    <>
      <WorkbenchContextBar />
      <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {breadcrumb}
          <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={h2Style}>{selectedEq!.name}</h2>
            <p style={descStyle}>{selectedEq!.meaning}</p>

            {/* Equation display */}
            <div style={equationBox}>
              {selectedEq!.symbolic_form}
            </div>

            {/* Solve for selector */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Solve for</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {selectedEq!.variables.map((v) => (
                  <button
                    key={v.symbol}
                    onClick={() => { setSolveFor(v.symbol); setResult(null); setSolveError(''); }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: solveFor === v.symbol ? '1px solid #f59e0b' : '1px solid var(--card-border)',
                      background: solveFor === v.symbol ? 'rgba(245,158,11,0.1)' : 'var(--card-bg)',
                      color: solveFor === v.symbol ? '#f59e0b' : 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontFamily: 'monospace',
                      fontWeight: 600,
                    }}
                    title={v.name}
                  >
                    {v.symbol}
                  </button>
                ))}
              </div>
            </div>

            {/* Input fields for known variables */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Known values</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {otherVars.map((v) => (
                  <div key={v.symbol} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: '#f59e0b', minWidth: 60 }}>
                      {v.symbol}
                    </span>
                    <input
                      type="number"
                      step="any"
                      value={values[v.symbol] || ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [v.symbol]: e.target.value }))}
                      placeholder={v.name}
                      onKeyDown={(e) => { if (e.key === 'Enter') runSolve(); }}
                      style={inputStyle}
                    />
                    <input
                      type="text"
                      value={units[v.symbol] || v.default_unit}
                      onChange={(e) => setUnits((prev) => ({ ...prev, [v.symbol]: e.target.value }))}
                      style={{ ...inputStyle, width: 120, flex: 'none' }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Solve button */}
            <button
              onClick={runSolve}
              disabled={isRunning}
              style={{ ...primaryBtnStyle, opacity: isRunning ? 0.5 : 1 }}
            >
              {isRunning ? 'Solving...' : `Solve for ${solveFor}`}
            </button>

            {isRunning && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                <div style={spinnerStyle} /><span style={{ color: '#f59e0b' }}>Computing...</span>
              </div>
            )}
            {solveError && <div style={errorStyle}>{solveError}</div>}

            {/* Result */}
            {result && (
              <div style={{ marginTop: 20 }}>
                {/* Primary result card */}
                <div style={resultHighlight}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                    {result.solve_for}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'monospace', color: '#f59e0b' }}>
                    {result.value_sigfig}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', fontFamily: 'monospace', marginTop: 2 }}>
                    {result.unit}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    {result.significant_figures} significant figure{result.significant_figures !== 1 ? 's' : ''}
                  </div>
                </div>

                {/* Working */}
                {result.working && (
                  <div style={{ marginTop: 12 }}>
                    <div style={sectionLabel}>Working</div>
                    <div style={workingBox}>
                      {result.working.symbolic_form && (
                        <div style={workingLine}>{result.working.symbolic_form}</div>
                      )}
                      {result.working.solved_expression && (
                        <div style={workingLine}>{result.working.solved_expression}</div>
                      )}
                      {result.working.substituted_expression && (
                        <div style={{ ...workingLine, color: '#f59e0b' }}>{result.working.substituted_expression}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {result.warnings.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={sectionLabel}>Warnings</div>
                    {result.warnings.map((w, i) => (
                      <div key={i} style={warningStyle}>{w}</div>
                    ))}
                  </div>
                )}

                {/* Assumptions */}
                {result.assumptions.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={sectionLabel}>Assumptions</div>
                    <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--text-muted)', fontSize: 12 }}>
                      {result.assumptions.map((a, i) => <li key={i} style={{ marginBottom: 2 }}>{a}</li>)}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button onClick={() => navigator.clipboard.writeText(JSON.stringify(result, null, 2))} style={btnStyle}>Copy JSON</button>
                  <button onClick={() => {
                    const lines = [
                      `Equation,${result.equation_name}`,
                      `Solve For,${result.solve_for}`,
                      `Value,${result.value}`,
                      `Unit,${result.unit}`,
                      `Sig Figs,${result.significant_figures}`,
                      `Value (sigfig),${result.value_sigfig}`,
                    ];
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = `${result.equation_id}_result.csv`; a.click(); URL.revokeObjectURL(url);
                  }} style={primaryBtnStyle}>Download CSV</button>
                </div>
              </div>
            )}
          </div>

          {/* Method card sidebar */}
          <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MethodCard
              engine="SymPy"
              title={selectedEq!.name}
              description={selectedEq!.meaning}
              equation={selectedEq!.symbolic_form}
              parameters={selectedEq!.variables.map((v) => ({
                name: v.symbol,
                desc: `${v.name} (${v.default_unit})`,
              }))}
              source="SymPy + Pint"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const breadcrumbBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontFamily: 'monospace', fontSize: 12, padding: 0 };
const h2Style: React.CSSProperties = { color: 'var(--text-primary)', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const labelStyle: React.CSSProperties = { display: 'block', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
const inputStyle: React.CSSProperties = { flex: 1, padding: '8px 12px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' as const };
const domainCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 200ms ease' };
const eqCardStyle: React.CSSProperties = { ...domainCardStyle, width: '100%' };
const countBadge: React.CSSProperties = { fontSize: 11, padding: '2px 7px', borderRadius: 5, background: 'rgba(245,158,11,0.12)', color: '#fbbf24', fontFamily: 'monospace', fontWeight: 500 };
const tagStyle: React.CSSProperties = { fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(13,148,136,0.08)', color: '#5eead4', fontFamily: 'monospace' };
const equationBox: React.CSSProperties = { padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, fontFamily: 'monospace', fontSize: 15, color: '#f59e0b', fontWeight: 600, marginBottom: 20, textAlign: 'center' };
const resultHighlight: React.CSSProperties = { padding: '16px 20px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 };
const sectionLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
const workingBox: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, padding: '10px 14px' };
const workingLine: React.CSSProperties = { fontFamily: 'monospace', fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.8 };
const warningStyle: React.CSSProperties = { padding: '6px 10px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 6, color: '#f59e0b', fontSize: 12, marginBottom: 4 };

export default WorkbenchCalcs;
